import React, { useState, useEffect } from "react";
import { Resizable } from "re-resizable";
import { globalMenu } from "@shared/libs/Global";

interface SnResizableProps {
  id: string;
  direction: "height" | "width";
  orgSize: string;
  children?: React.ReactNode;
}

export function SnResizable({
  id,
  direction,
  orgSize,
  children,
}: SnResizableProps) {
  const { activeMenuInfo } = globalMenu();
  const cdMenu = activeMenuInfo.cd_menu;

  const [size, setSize] = useState<string>();

  // context menu
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
  } | null>(null);

  // 최초 로딩
  useEffect(() => {
    const menuSize = localStorage.getItem("menuSize");

    if (menuSize) {
      const parsed = JSON.parse(menuSize);
      if (parsed[cdMenu] && parsed[cdMenu][id]) {
        setSize(parsed[cdMenu][id]);
      } else {
        setSize(orgSize);
      }
    } else {
      setSize(orgSize);
    }
  }, [orgSize, cdMenu, id]);

  // 외부 클릭 시 context menu 닫기
  useEffect(() => {
    if (!contextMenu?.visible) return;
    const close = () => setContextMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [contextMenu]);

  // Resize Stop
  const handleResizeStop = (
    event: MouseEvent | TouchEvent,
    dir: string,
    ref: HTMLElement
  ) => {
    const newSize = direction === "height" ? ref.style.height : ref.style.width;

    let newMenuSize = {};
    const menuSize = localStorage.getItem("menuSize");

    if (menuSize) {
      const parsed = JSON.parse(menuSize);
      newMenuSize = {
        ...parsed,
        [cdMenu]: {
          ...(parsed[cdMenu] || {}),
          [id]: newSize,
        },
      };
    } else {
      newMenuSize = {
        [cdMenu]: { [id]: newSize },
      };
    }

    setSize(newSize);
    localStorage.setItem("menuSize", JSON.stringify(newMenuSize));
  };

  // 우클릭 핸들러
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleResetSize = () => {
    setSize(orgSize);

    const menuSize = localStorage.getItem("menuSize");
    let newMenuSize = {};

    if (menuSize) {
      const parsed = JSON.parse(menuSize);
      newMenuSize = {
        ...parsed,
        [cdMenu]: {
          ...(parsed[cdMenu] || {}),
          [id]: orgSize,
        },
      };
    } else {
      newMenuSize = {
        [cdMenu]: { [id]: orgSize },
      };
    }

    localStorage.setItem("menuSize", JSON.stringify(newMenuSize));
    setContextMenu(null);
  };

  // height 방향만 커스터마이즈 (단 1개의 핸들러)
  const handleComponent =
    direction === "height"
      ? {
          bottom: (
            <div
              onContextMenu={handleContextMenu}
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                height: "12px", // 넉넉한 핸들러
                cursor: "ns-resize", // 항상 동일 커서
                background: "transparent",
              }}
            />
          ),
        }
      : {
          right: (
            <div
              onContextMenu={handleContextMenu}
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                height: "100%",
                width: "12px",
                cursor: "ew-resize",
                background: "transparent",
              }}
            />
          ),
        };

  return (
    <Resizable
      size={direction === "height" ? { height: size } : { width: size }}
      minHeight="55px"
      minWidth="100px"
      onResizeStop={handleResizeStop}
      enable={direction === "height" ? { bottom: true } : { right: true }}
      handleComponent={handleComponent}
      style={{ position: "relative" }}
    >
      {children}

      {/* context menu */}
      {contextMenu?.visible && (
        <div
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            border: "1px solid #ccc",
            background: "#fff",
            boxShadow: "1px 1px 5px rgba(0,0,0,0.2)",
          }}
        >
          <div
            style={{
              padding: "6px 14px",
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
            onClick={handleResetSize}
          >
            사이즈 초기화
          </div>
        </div>
      )}
    </Resizable>
  );
}

export default React.memo(SnResizable);
